import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ClientRegister() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", company_name: "", industry: "", website_url: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Normalize a user-entered website: allow "example.com" (no protocol) and
  // auto-prepend "https://" so what we save is always a real URL.
  const normalizeWebsiteUrl = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const payload = { ...form, website_url: normalizeWebsiteUrl(form.website_url) };
    try {
      await api.post("/auth/register/client", payload);
      await refresh();
      nav("/client");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="client-register-page">
      <Navigation />
      <div className="max-w-xl mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">For Brands</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2">Launch a Study.</h1>
        <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Create your corporate account. Stripe-powered checkout is wired behind the scenes for instant campaign launch.</p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="client-register-form">
          {[
            ["company_name", "Company name", "text", ""],
            ["email", "Work email", "email", ""],
            ["password", "Password", "password", ""],
            ["website_url", "Website URL", "text", "example.com"],
          ].map(([k, label, type, placeholder]) => (
            <div key={k}>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">{label}</label>
              <input
                type={type}
                required
                value={form[k]}
                onChange={set(k)}
                placeholder={placeholder}
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                data-testid={`client-input-${k}`}
              />
              {k === "website_url" && (
                <div className="mt-1 text-[11px] text-[var(--rl-text-secondary)]" data-testid="website-url-hint">
                  You can enter your domain without https:// — we&apos;ll add it automatically.
                </div>
              )}
            </div>
          ))}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Industry</label>
            <select
              required
              value={form.industry}
              onChange={set("industry")}
              className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
              data-testid="client-input-industry"
            >
              <option value="">Select industry…</option>
              {["Consumer Goods","Financial Services","Healthcare","Retail","Real Estate","Technology","Media & Entertainment","Government / Public Sector","Other"].map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
          </div>
          {error && <div className="text-sm text-[var(--rl-accent)] font-medium" data-testid="client-register-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
            data-testid="client-register-submit"
          >
            {loading ? "Creating account…" : "Create client account"}
          </button>
        </form>
      </div>
    </div>
  );
}
